import { Dispatch } from "react";
import { captureMessage } from "@sentry/react";
import axios from "axios";
import { TFunction } from "i18next";
import type { PickedAuthInstance } from "stores/useAuth";
import type { StorageSize } from "hooks/useImageUpload";
import {
  OptionsActions,
  updateNonSaveableOptions,
  updateSaveableOptions,
} from "stores/optionsStore/actions";
import { MantineColorScheme } from "@mantine/core";
import {
  getMissingSaveableOptions,
  initialOptionsState,
  pickSaveableOptions,
} from "stores/optionsStore/state";
import { showErrorNotification } from "utils/notifications/customNotifications";
import { isColorScheme } from "utils/validators/isColorScheme";

type Dependencies = {
  auth: PickedAuthInstance & { user: NonNullable<PickedAuthInstance["user"]> };
  dispatchOptions: Dispatch<OptionsActions>;
  /** The current options, read once the settings are loaded */
  getOptions: () => typeof initialOptionsState;
  getStorageSize: () => Promise<StorageSize>;
  setColorScheme: (colorScheme: MantineColorScheme) => void;
  t: TFunction;
};

/**
 * Loads what the app needs of a signed in user, once after signing in or opening the app signed in: the subscription
 * (the premium features), the settings and the used storage. The requests run side by side; a 401 from any of them
 * means the session expired and signs the user out, other failures are reported.
 */
export const loadSignedInUserData = async ({
  auth: { getAppSubscriptionStatus, getUserSettings, user, signOut, update },
  dispatchOptions,
  getOptions,
  getStorageSize,
  setColorScheme,
  t,
}: Dependencies) => {
  // Without a saved color scheme the current one is kept ("auto" follows the system)
  if (isColorScheme(user.settings?.colorScheme)) {
    setColorScheme(user.settings.colorScheme);
  }

  const loadSubscription = async () => {
    const { data } = await getAppSubscriptionStatus(
      user.settings?.language ?? navigator.language
    );
    const { errors, state } = data;

    if (state === "active" || state === "inGracePeriod") {
      dispatchOptions(updateNonSaveableOptions({ allowPremiumFeatures: true }));
    } else if (state === "inactive") {
      dispatchOptions(
        updateNonSaveableOptions({ allowPremiumFeatures: false })
      );
    } else if (errors && errors.length > 0) {
      captureMessage(
        `Trackbook: Errors when getting info about subscription status: ${errors.join(" ")}`
      );
    }
    if (state === "inGracePeriod") {
      showErrorNotification({
        title: t("errors.subscription_in_grace_period.title"),
        message: t("errors.subscription_in_grace_period.text"),
      });
    }
  };

  const loadSettings = async () => {
    const settings = await getUserSettings();
    if (isColorScheme(settings?.colorScheme)) {
      setColorScheme(settings.colorScheme);
    }
    const savedOptions = pickSaveableOptions(settings);
    dispatchOptions(updateSaveableOptions(savedOptions));

    // An account without a map or units (e.g. a new one) keeps the current ones, which are saved
    // so the next sign in restores them. A failed save shows a notification.
    const missingOptions = getMissingSaveableOptions(
      savedOptions,
      getOptions()
    );
    if (Object.keys(missingOptions).length > 0) {
      update(missingOptions).catch(() => undefined);
    }
  };

  const loadStorageSize = async () => {
    const storageSize = await getStorageSize();
    dispatchOptions(updateNonSaveableOptions({ storageSize }));
  };

  const settled = await Promise.allSettled([
    loadSubscription(),
    loadSettings(),
    loadStorageSize(),
  ]);

  for (const result of settled) {
    if (result.status === "fulfilled") continue;
    const error: unknown = result.reason;

    if (axios.isAxiosError(error) && error.response?.status === 401) {
      void signOut();
      return;
    }
    captureMessage(
      error instanceof Error ? error.message : `Trackbook: ${String(error)}`
    );
  }
};
